import { useState } from "react";
import { DIAL_CODES } from "../utils/phone.js";
import { now, toSlug } from "../utils/slug.js";
import { generatePersonaDetails } from "../utils/api.js";
import { generatePersonaDraft, createPersonaWithKb, validatePersona } from "../services/candidates.js";
import { CreatePersonaStep1 } from "./CreatePersonaStep1.jsx";
import { CreatePersonaStep2 } from "./CreatePersonaStep2.jsx";

export function CreatePersona({ onCreated, onBack }) {
  // ── Step: 1=form, 2=kb-preview, 3=saving ─────────────────────────────────
  const [step, setStep] = useState(1);

  // ── Form fields ───────────────────────────────────────────────────────────
  // v1.27.0: `party` removed from the create form. `partyColor` renamed to
  // `accentColor` — still a user-picked theme colour, just no longer framed
  // as "party colour". The DB column is `accent_color` (migration 034).
  //
  // v1.28.0 (AGCY): personaType read from sessionStorage (set by the list
  // page's "+ New" button) so the form defaults to the right type. The
  // value is passed to createPersonaWithKb in the submit handler below.
  // Default 'official' keeps behaviour unchanged if sessionStorage is
  // empty (direct-deep-link to /admin/create).
  const initialPersonaType =
    typeof window !== "undefined" && sessionStorage.getItem("qm_create_persona_type") === "agency"
      ? "agency"
      : "official";
  const [form, setForm] = useState({
    name: "", role: "", accentColor: "#2563eb",
    personaType: initialPersonaType,
    // v1.28.2 — short name for the voter-facing CTA ("Ask [short_name] a
    // Question"). Left blank → the client falls back to a name-splitting
    // heuristic at render time. Admin is nudged to fill it for agencies
    // (acronyms like "NDDC") where the heuristic produces poor output.
    shortName: "",
    location: "", bio: "", statement: "", surveyStatement: "", manualStatement: "",
    geoCountry: "", geoState: "", geoLga: "",
  });
  const [busy, setBusy] = useState(false);
  const [prefilling, setPrefilling] = useState(false);
  const [prefillErr, setPrefillErr] = useState(null);
  const [statementMode, setStatementMode] = useState("survey"); // "survey"|"manual"|"preview"

  // ── KB entry drafts (Step 2) ──────────────────────────────────────────────
  const [kbDrafts, setKbDrafts] = useState([]); // { id, topic, content, source_title, source_url, checked }
  const [saveErr, setSaveErr] = useState(null);

  const ok = !!(form.name && (form.surveyStatement || form.manualStatement || form.statement));

  // ── AI pre-fill ───────────────────────────────────────────────────────────
  const aiFill = async () => {
    if (!form.name || prefilling) return;
    setPrefilling(true);
    setPrefillErr(null);
    try {
      const countryName = DIAL_CODES.find(d => d.code === form.geoCountry)?.country;
      const draft = await generatePersonaDraft(form.name, countryName || form.geoCountry, form.role);
      setForm(f => ({ ...f, bio: draft.bio || f.bio, surveyStatement: draft.statement || f.surveyStatement }));
      if (draft.statement) setStatementMode("preview");
      if (Array.isArray(draft.knowledgeEntries) && draft.knowledgeEntries.length > 0) {
        setKbDrafts(draft.knowledgeEntries.map((e, i) => ({
          id: `draft-${i}`, topic: e.topic || "", content: e.content || "",
          source_title: e.source_title || "", source_url: e.source_url || "", checked: true,
        })));
      }
    } catch {
      setPrefillErr("AI pre-fill failed — please fill in manually.");
    } finally {
      setPrefilling(false);
    }
  };

  // ── KB draft mutations ────────────────────────────────────────────────────
  const updateKbDraft = (id, field, value) =>
    setKbDrafts(prev => prev.map(e => e.id === id ? { ...e, [field]: value } : e));
  const toggleKbDraft = id =>
    setKbDrafts(prev => prev.map(e => e.id === id ? { ...e, checked: !e.checked } : e));
  const removeKbDraft = id =>
    setKbDrafts(prev => prev.filter(e => e.id !== id));

  // ── Validation state (shown on Step 1) ─────────────────────────────────
  const [validating, setValidating] = useState(false);
  const [validationErr, setValidationErr] = useState(null);

  // ── Advance to KB preview — validate persona first ────────────────────
  const goToKbPreview = async () => {
    if (!ok) return;
    setValidating(true);
    setValidationErr(null);
    try {
      const composedStatement = [form.surveyStatement, form.manualStatement || form.statement].filter(Boolean).join("\n\n");
      const slug = toSlug(form.name);
      await validatePersona({
        name: form.name, slug, role: form.role || undefined,
        statement: composedStatement,
        geo_country: form.geoCountry || undefined,
        geo_state: form.geoState || undefined,
        geo_lga: form.geoLga || undefined,
        geo_lgas: form.geoLgas ?? [],
        // v1.28.0 (AGCY) — pass type through so validation (superRefine's
        // role-based geo rules) skips for agencies.
        persona_type: form.personaType,
      });
      // Validation passed — proceed
      if (kbDrafts.length === 0) { confirmSave([]); return; }
      setStep(2);
    } catch (e) {
      const msg = e?.message ?? "Validation failed";
      setValidationErr(msg);
    } finally {
      setValidating(false);
    }
  };

  // ── Save persona + selected KB entries ────────────────────────────────────
  const confirmSave = async (selectedEntries) => {
    setBusy(true);
    setSaveErr(null);
    setStep(3);
    try {
      const composedStatement = [form.surveyStatement, form.manualStatement || form.statement].filter(Boolean).join("\n\n");
      const details = await generatePersonaDetails(composedStatement);
      const slug = toSlug(form.name);
      const avatar = form.name.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
      const result = await createPersonaWithKb(
        {
          name: form.name, slug, role: form.role || undefined,
          accent_color: form.accentColor,
          // v1.28.0 (AGCY) — thread the type through to the API so the new row
          // is marked correctly. Server defaults to 'official' if omitted.
          persona_type: form.personaType,
          // v1.28.2 — short name for CTA. Empty string → null server-side
          // so the client coalesces to the fallback heuristic on render.
          short_name: form.shortName?.trim() || null,
          location: form.location || undefined, bio: form.bio || undefined,
          statement: composedStatement, tagline: details.tagline,
          suggested_prompts: details.suggestedPrompts, key_policies: details.keyPolicies,
          avatar, photo_url: "",
          geo_country: form.geoCountry || undefined,
          geo_state: form.geoState || undefined,
          geo_lga: form.geoLga || undefined,
          geo_lgas: form.geoLgas ?? [],
        },
        selectedEntries.map(({ topic, content, source_title, source_url }) => ({ topic, content, source_title, source_url }))
      );
      onCreated({
        ...result.persona,
        conversations: [], feedback: [],
        createdAt: result.persona.created_at ?? now(),
        keyPolicies: result.persona.key_policies ?? details.keyPolicies,
        suggestedPrompts: result.persona.suggested_prompts ?? details.suggestedPrompts,
        tagline: result.persona.tagline ?? details.tagline,
      });
    } catch (e) {
      const msg = e?.message ?? "unknown error";
      const friendly = msg.includes("SLUG_EXISTS") || msg.includes("slug already exists")
        ? "A persona with this URL already exists. Go back and rename."
        : msg.includes("ROLE_GEO_EXISTS") || msg.includes("already exists for")
        ? msg.replace(/.*?(".*?")/, "$1").replace(/^"/, "").replace(/"$/, "") || msg
        : msg;
      setSaveErr("Failed to save — " + friendly);
      setStep(2);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ maxWidth: 640, margin: "0 auto", padding: "32px 18px 60px" }}>
      <button onClick={step > 1 ? () => setStep(s => s - 1) : onBack}
        style={{ background: "none", border: "none", color: "#64748b", cursor: "pointer", fontSize: 13, marginBottom: 24, padding: 0 }}>
        ← {step > 1 ? "Back to form" : "Back"}
      </button>

      <div style={{ marginBottom: 28 }}>
        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.15em", color: "#3b82f6", textTransform: "uppercase", marginBottom: 7 }}>Admin Console</div>
        {/* v1.28.0 (AGCY): title adapts to the persona type being created */}
        <h1 style={{ fontSize: 26, fontWeight: 900, color: "#f1f5f9", margin: 0, fontFamily: "Georgia,serif" }}>
          {form.personaType === "agency" ? "Create an Agency" : "Create a Digital Clone"}
        </h1>
        <p style={{ color: "#64748b", marginTop: 7, fontSize: 13, lineHeight: 1.6 }}>
          {step === 1 && <>Profile at <span style={{ color: "#3b82f6", fontFamily: "monospace" }}>{(import.meta.env.VITE_APP_URL ?? "").replace(/^https?:\/\//, "").replace(/\/$/, "") || "questionme.io"}/slug</span></>}
          {step === 2 && "Review and curate AI-generated knowledge base entries"}
          {step === 3 && "Saving your clone to the database…"}
        </p>
        <div style={{ display: "flex", gap: 6, marginTop: 12, alignItems: "center" }}>
          {[["Profile", 1], ["Knowledge", 2], ["Saving", 3]].map(([label, s]) => (
            <div key={s} style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                <div style={{ width: 28, height: 4, borderRadius: 3,
                  background: s <= step ? "#3b82f6" : "#1e2d45", transition: "background .2s" }} />
                <span style={{ fontSize: 10, fontWeight: s === step ? 700 : 400,
                  color: s === step ? "#3b82f6" : s < step ? "#64748b" : "#334155",
                  whiteSpace: "nowrap" }}>{label}</span>
              </div>
              {s < 3 && <span style={{ color: "#1e2d45", fontSize: 10, margin: "0 2px" }}>›</span>}
            </div>
          ))}
        </div>
      </div>

      {step === 1 && (
        <CreatePersonaStep1
          form={form} setForm={setForm}
          statementMode={statementMode} setStatementMode={setStatementMode}
          kbDrafts={kbDrafts} prefilling={prefilling} prefillErr={prefillErr}
          ok={ok} onAiFill={aiFill} onNext={goToKbPreview}
          validating={validating} validationErr={validationErr}
        />
      )}

      {step === 2 && (
        <CreatePersonaStep2
          kbDrafts={kbDrafts} busy={busy} saveErr={saveErr}
          onUpdate={updateKbDraft} onToggle={toggleKbDraft}
          onRemove={removeKbDraft} onSave={confirmSave}
        />
      )}

      {step === 3 && (
        <div style={{ textAlign: "center", padding: "40px 0" }}>
          <div style={{ fontSize: 42, marginBottom: 16, animation: "spin 1.5s linear infinite", display: "inline-block" }}>⟳</div>
          <div style={{ color: "#94a3b8", fontSize: 14 }}>Saving clone and generating embeddings…</div>
          <div style={{ color: "#475569", fontSize: 12, marginTop: 8 }}>This may take 10–20 seconds</div>
        </div>
      )}
    </div>
  );
}

export default CreatePersona;
